import type { ElementType, ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * Cartão é a resposta preguiçosa — use quando o conteúdo é mesmo um bloco
 * destacável, e nunca dentro de outro cartão.
 *
 * Uma anatomia só, para as duas aplicações. O portal desenhava a sua na mão
 * (`<h2 className="text-sm font-medium">` solto dentro do corpo) enquanto a
 * interna usava o cabeçalho do componente, com outro peso de fonte: mesma
 * peça, dois desenhos, que é o defeito que a decisão 3 do DESIGN.md proíbe.
 *
 * A linha entre cabeçalho e corpo aparece só em `sangria` — conteúdo que vai
 * de borda a borda, como tabela. Separar cabeçalho de um parágrafo que já tem
 * respiro é decoração, e a linha vira ruído.
 *
 * ## As sete anatomias, medidas em 17/09/2026
 *
 * Este componente existia e **a casca dele estava copiada em sete lugares**,
 * cada cópia com uma forma e um recuo:
 *
 * | peça                                   | forma            | sombra | recuo         |
 * | -------------------------------------- | ---------------- | ------ | ------------- |
 * | `Card`                                 | `rounded-cartao` | sim    | `px-4 pt-3.5` |
 * | `FilterCard`                           | `rounded-grande` | sim    | `p-4`         |
 * | `TargetCard` · `TargetTrend`           | `rounded-grande` | sim    | `p-4`         |
 * | `CategoryColumn` (painel do consultor) | `rounded-cartao` | não    | `p-5`         |
 * | `AwardCard`                            | `rounded-cartao` | não    | `p-4`         |
 * | item da lista de demandas (portal)     | `rounded-cartao` | sim    | `px-4 py-3.5` |
 * | `RequestCard` · `ServiceCaseCard`      | `rounded-borda`  | não    | `p-3`         |
 *
 * Na interna `--radius-cartao` e `--radius-grande` valem os mesmos 12px, então
 * metade da divergência era **latente**: ela só apareceria no portal, onde o
 * cartão é 18px e o painel 12. Divergência que não se vê é a que sobrevive —
 * nenhum teste pegava nada disso, porque cada peça isolada estava certa.
 *
 * ## `cardClasses` existe para quem NÃO pode ser um `<section>`
 *
 * O item da lista de demandas do portal é um `<a>` inteiro, e o do quadro da
 * retenção é um bloco com link, selo e três blocos de texto — a anatomia de
 * cabeçalho, descrição e ações não é a deles. Antes eles copiavam as classes;
 * agora pedem a casca pela função, que é o mesmo caminho de `linkTextClasses` e
 * `buttonClasses`. A régua `cartao-a-mao` derruba a oitava cópia.
 */

/** Em que fundo o cartão pousa. `suave` é o cartão que vive dentro de outro bloco. */
export type CardSurface = 'superficie' | 'suave';

/**
 * O recuo. `densa` é o cartão de coluna estreita — o do quadro da retenção e o
 * da mesa de cobrança, onde trinta peças empilham na mesma tela.
 */
export type CardDensity = 'normal' | 'densa';

/**
 * Quando o cartão INTEIRO carrega um estado: o selecionado da mesa, o grupo que
 * pede ação no painel do consultor.
 *
 * Tinge a borda **e** o fundo, como o `Badge` — borda colorida sozinha nunca é
 * intenção, e é o que o `DESIGN.md` chama de "borda lateral colorida" com outro
 * nome. Fora desses casos o tom é `neutro`, e a cor do cartão não diz nada: cor
 * gasta em decoração é cor que não sobra para o estado.
 */
export type CardTone = 'neutro' | 'primaria' | 'sucesso' | 'alerta' | 'erro';

const SURFACES: Record<CardSurface, string> = {
  superficie: 'bg-superficie',
  suave: 'bg-superficie-suave',
};

const TONES: Record<CardTone, string> = {
  neutro: 'border-borda',
  primaria: 'border-primaria-texto bg-primaria-suave',
  sucesso: 'border-sucesso bg-sucesso-suave',
  alerta: 'border-alerta bg-alerta-suave',
  erro: 'border-erro bg-erro-suave',
};

/**
 * A casca do cartão, para quem precisa dela em outro elemento.
 *
 * `rounded-cartao` (12px na interna, 18px no portal) e sombra: no desenho de
 * 05/09 o cartão é a única peça que se levanta da página, e é isso que faz uma
 * pilha de cartões ler como pilha em vez de como uma folha dividida por traços.
 *
 * A curvatura é `cartao` e não `grande` porque as duas aplicações discordam
 * sobre ela: no portal `--radius-grande` é 24px — o canto do BLOCO do cabeçalho
 * — e o cartão é 18px. Com `rounded-grande` aqui, todo cartão do portal saía
 * com o raio da moldura.
 */
export function cardClasses({
  surface = 'superficie',
  density = 'normal',
  tone = 'neutro',
  interactive = false,
  padding = true,
  className,
}: {
  surface?: CardSurface;
  density?: CardDensity;
  tone?: CardTone;
  /** Cartão que responde ao ponteiro — o que é link ou o que abre um painel. */
  interactive?: boolean;
  /** `false` para quem desenha o próprio recuo por região, como o `Card`. */
  padding?: boolean;
  className?: string;
} = {}) {
  return cn(
    'rounded-cartao border shadow-cartao',
    tone === 'neutro' ? cn('border-borda', SURFACES[surface]) : TONES[tone],
    padding && (density === 'densa' ? 'p-3' : 'p-4'),
    interactive && 'transition-colors hover:border-borda-forte',
    className,
  );
}

export function Card({
  title,
  descricao,
  actions,
  children,
  level = 2,
  bleed = false,
  as: As = 'section',
  surface = 'superficie',
  density = 'normal',
  tone = 'neutro',
  className,
}: {
  title?: ReactNode;
  descricao?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** O nível do título. A ordem dos cabeçalhos da página é de quem monta a página. */
  level?: 2 | 3;
  /** Corpo sem recuo, para tabela e lista que encostam na borda. */
  bleed?: boolean;
  /**
   * O elemento. `figure` é o do gráfico, que tem legenda; `div` é o do cartão
   * que já vive dentro de um `<li>` e não quer um segundo marco de região.
   */
  as?: Extract<ElementType, 'section' | 'div' | 'article' | 'figure'>;
  surface?: CardSurface;
  density?: CardDensity;
  tone?: CardTone;
  className?: string;
}) {
  const Title = level === 2 ? 'h2' : 'h3';
  const hasHeader = Boolean(title || actions);
  /*
   * O recuo do corpo é o mesmo da casca, escrito por região: o cabeçalho tem um
   * respiro menor embaixo do que o corpo tem em cima.
   */
  const inset = density === 'densa' ? 'px-3' : 'px-4';

  return (
    <As
      className={cardClasses({
        surface,
        density,
        tone,
        padding: false,
        /*
         * Sangria recorta: sem isto a primeira linha de uma tabela passa por
         * cima do canto arredondado do cartão.
         */
        className: cn(bleed && 'overflow-hidden', className),
      })}
    >
      {hasHeader && (
        <header
          className={cn(
            'flex items-start justify-between gap-4',
            inset,
            density === 'densa' ? 'pt-3' : 'pt-3.5',
            // `borda-suave`: o cabeçalho separa DENTRO do cartão, e a borda de
            // contorno repetida aqui desenharia um segundo cartão lá dentro.
            bleed ? 'border-b border-borda-suave pb-3.5' : 'pb-3',
          )}
        >
          <div className="min-w-0">
            {title && (
              <Title className="text-[0.8125rem] font-bold text-texto">{title}</Title>
            )}
            {descricao && <p className="mt-0.5 text-xs text-texto-suave">{descricao}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </header>
      )}
      <div
        className={cn(
          bleed ? '' : cn(inset, density === 'densa' ? 'pb-3' : 'pb-4'),
          !hasHeader && !bleed && (density === 'densa' ? 'pt-3' : 'pt-4'),
        )}
      >
        {children}
      </div>
    </As>
  );
}
